import { Directive, Input, TemplateRef, ViewContainerRef } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { type IFeature } from '@principle-theorem/feature-flags';
import { isString } from 'lodash';
import { noop, of, ReplaySubject } from 'rxjs';
import { map, switchMap, tap } from 'rxjs/operators';
import { FeatureFlagsService } from './feature-flags.service';

@Directive({
  selector: '[ptFeatureEnabled]',
})
export class FeatureEnabledDirective {
  private _thenTemplateRef: TemplateRef<unknown>;
  private _templateRendered = false;
  featureLabel$ = new ReplaySubject<string | undefined>(1);

  @Input('ptFeatureEnabled')
  set featureLabel(label: IFeature | string | undefined) {
    this.featureLabel$.next(
      isString(label) ? label : label?.label || undefined
    );
  }

  constructor(
    private _viewContainer: ViewContainerRef,
    templateRef: TemplateRef<unknown>,
    private _featureFlags: FeatureFlagsService
  ) {
    this._thenTemplateRef = templateRef;
    this._viewContainer.clear();

    this.featureLabel$
      .pipe(
        switchMap((label) => {
          if (!label) {
            return of(true);
          }

          return this._featureFlags.isFeatureEnabledByLabel$(label).pipe(
            map((isEnabled) => {
              if (!isEnabled) {
                return false;
              }
              return true;
            })
          );
        }),
        tap((showTemplate) => this._update(showTemplate)),
        takeUntilDestroyed()
      )
      .subscribe(noop);
  }

  private _update(showTemplate: boolean): void {
    if (this._templateRendered === showTemplate) {
      return;
    }
    if (showTemplate) {
      return this._addTemplate();
    }
    this._removeTemplate();
  }

  private _removeTemplate(): void {
    this._viewContainer.clear();
    this._templateRendered = false;
  }

  private _addTemplate(): void {
    this._viewContainer.createEmbeddedView(this._thenTemplateRef);
    this._templateRendered = true;
  }
}
